import React from 'react';
import Alert from '@amzn/meridian/alert';
import Column from '@amzn/meridian/column';
import { useSiteGatedFeature } from '../../../hooks/useSiteGatedFeature';
import FlowMap from './index';

/** Allowlist-gated in the facade via FeatureGating.RCC_FLOW_MAP.AllowedUsers; also gates the Buffers page. */
export const FLOW_MAP_FEATURE_NAME = 'RCC_FLOW_MAP';

/**
 * Feature gate for the Visual Flow Map page (the Andon cards and Volume Flow map).
 *
 * The page is reachable by URL and from the sidebar, and neither of those is permission-aware:
 * the sidebar's `permittedRolesList` is carried through ReloBoardServer's model but never
 * enforced, so visibility of the link is not a control. This gate is what actually restricts
 * who can see the page.
 *
 * Resolution follows the established dashboard / AgentChatPanelGated pattern:
 * `null` while the facade round-trip is in flight (render nothing rather than flashing the
 * page and retracting it), `true` renders the page, `false` explains why it is unavailable
 * instead of leaving a blank board. Local dev short-circuits to enabled inside
 * {@link useSiteGatedFeature}, since getUserName() cannot resolve without a Boson parent frame.
 *
 * Fails closed: an error resolving the flag yields `false`.
 */
const FlowMapGated: React.FC = () => {
  const isEnabled = useSiteGatedFeature(FLOW_MAP_FEATURE_NAME);

  if (isEnabled === null) {
    return null;
  }

  if (!isEnabled) {
    return (
      <Column spacing="400" spacingInset="500" data-testid="flow-map-gated">
        <Alert type="neutral" data-testid="flow-map-not-enabled">
          The Visual Flow Map is coming soon and not yet available.
        </Alert>
      </Column>
    );
  }

  return <FlowMap />;
};

export default FlowMapGated;
